import {
    AfterContentInit,
    Component,
    ContentChild,
    HostBinding,
    Inject,
    Input,
    OnDestroy,
    Optional
} from "@angular/core";
import {debounceTime, distinctUntilChanged, Subject, Subscription} from "rxjs";
import {
    AbstractSearchControlComponent,
    SearchControlFieldSelector
} from "../../search-controls/abstract-search-control.component";
import {SEARCH_FORM_EXTENDED_CONTROLS_MODE, SearchFormExtendedService} from "../search-form-extended.service";
import {AbstractSearchModel} from "@fedeltapointofsale/data-client";
import {SearchControlSelectObjects} from "../../../select/search-control-select-objects";

@Component({
    selector: "wbo-search-form-field",
    styleUrls: ["./search-form-field.component.scss"],
    templateUrl: "./search-form-field.component.html"
})
export class SearchFormFieldComponent implements AfterContentInit, OnDestroy {

    @ContentChild(SearchControlFieldSelector) control: AbstractSearchControlComponent;

    /**
     * Field name used to set the SearchModel.filter
     */
    @Input() field: string;

    /**
     * Indicates that the field comes from extended control mode (pills)
     */
    @HostBinding("class.extended") extendedMode: boolean;

    private internalValueChanges: Subject<any> = new Subject<any>();

    private valueChangesEventSubscription: Subscription;

    private filterChangesEventSubscription: Subscription;

    private removedEventSubscription: Subscription;

    private formFieldRemovedSubscription: Subscription;

    constructor(public searchModel: AbstractSearchModel,
                @Optional() @Inject(SEARCH_FORM_EXTENDED_CONTROLS_MODE) searchExtendedMode,
                private searchFormExtendedService: SearchFormExtendedService) {
        if (searchExtendedMode) {
            this.extendedMode = true;

            // When field comes from extended fields
            this.field = searchFormExtendedService.currentExtendedField;

            // when this form field is removed clear the value on the filter
            this.formFieldRemovedSubscription = this.searchFormExtendedService.formFieldRemoved.subscribe(field => {
                if (field === this.field) {
                    this.searchModel.setFilterValue(this.field, undefined);
                }
            });
        }

        // When the filter changes set the control value
        this.filterChangesEventSubscription = searchModel.filterFieldChange.subscribe(event => {
            if (event.filterName === this.field) {
                this.setValueToControl(event.value);
            }
        });
    }

    private setValueToControl(value: any): void {
        if (value instanceof SearchControlSelectObjects) {
            this.control.setSearchControlSelectObjects(value);
        } else {
            this.control.writeValue(value);
        }
    }

    ngAfterContentInit(): void {
        this.control.label = "wbo." + this.searchModel.getResource() + ".search.field." + this.field + ".label";
        this.control.placeholder = this.extendedMode ? "" : "wbo." + this.searchModel.getResource() + ".search.field." + this.field + ".placeholder";
        this.control.elementId = "search-filter-control-" + this.field;

        this.control.registerOnChange((value) => this.internalValueChanges.next(Array.isArray(value) ? value.slice() : value));
        this.setValueToControl(this.searchModel.filter[this.field]);
        // register an event when the control is requested to be removed
        this.removedEventSubscription = this.control.removeControl.subscribe(() =>
            this.searchFormExtendedService.requestRemoveFormField(this.field));

        // when the control value changes set the filter value
        this.valueChangesEventSubscription = this.internalValueChanges.pipe(
            debounceTime(400),
            distinctUntilChanged()
        ).subscribe(term => {
            if (term === "") {
                term = undefined;
            }
            this.searchModel.setFilterValue(this.field, term);
        });
    }

    ngOnDestroy(): void {
        if (this.valueChangesEventSubscription) {
            this.valueChangesEventSubscription.unsubscribe();
        }
        if (this.filterChangesEventSubscription) {
            this.filterChangesEventSubscription.unsubscribe();
        }
        if (this.removedEventSubscription) {
            this.removedEventSubscription.unsubscribe();
        }
        if (this.formFieldRemovedSubscription) {
            this.formFieldRemovedSubscription.unsubscribe();
        }
    }
}
